import { useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Eye, ShieldAlert, X } from "lucide-react";
import { DIVISIONS, ROLE_LABEL } from "../lib/constants";
import { useAuth } from "../context/AuthContext";

// Semua role yang benar-benar dipakai di RBAC frontend/backend (ROLE_LABEL di constants.js).
// Owner sengaja tidak dimasukkan — Owner adalah role asli, keluar Preview untuk kembali ke Owner.
const PREVIEW_ROLES = [
  { role: "general_manager",  label: "General Manager",                   needsDivision: false },
  { role: "admin",            label: "Admin (Kantor Pusat)",              needsDivision: false },
  { role: "division_manager", label: "Manager Divisi",                    needsDivision: true },
  { role: "division_admin",   label: "Admin Divisi (Admin MUA = Kantor Pusat)", needsDivision: true },
  { role: "division_staff",   label: "Staf Divisi",                       needsDivision: true },
  { role: "content_creator",  label: "Content Creator",                   needsDivision: false },
];

export const RolePreviewButton = () => {
  const { realUser, viewAs, enterPreview } = useAuth();
  const [open, setOpen] = useState(false);
  const [role, setRole] = useState(viewAs?.role || "");
  const [divisionId, setDivisionId] = useState(viewAs?.division_id || "");
  const preset = useMemo(() => PREVIEW_ROLES.find((p) => p.role === role), [role]);

  if (!realUser || realUser.role !== "owner") return null;

  const canApply = role && (!preset?.needsDivision || divisionId);

  const apply = () => {
    if (!canApply) return;
    const chosen = PREVIEW_ROLES.find((p) => p.role === role);
    const label = chosen?.needsDivision
      ? `${chosen.label} — ${DIVISIONS.find((d) => d.id === divisionId)?.short || divisionId}`
      : chosen?.label;
    enterPreview({ role, division_id: preset?.needsDivision ? divisionId : null, label });
    setOpen(false);
  };

  return (
    <>
      <button onClick={() => setOpen(true)}
        className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-zinc-300 hover:bg-white/5 hover:text-white transition-colors"
        data-testid="btn-role-preview">
        <Eye className="w-4 h-4"/>
        <span className="flex-1 text-left truncate">Lihat Sebagai Peran</span>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="role-preview-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Eye className="w-4 h-4"/> Lihat Sebagai Peran</DialogTitle>
            <DialogDescription>
              Simulasi tampilan sisi lain: sidebar, menu, dan dashboard mengikuti permission peran yang dipilih. <b>Bukan login baru</b> — sesi Owner tidak berubah. Semua aksi ubah data (buat, edit, hapus, approve, catat pembayaran, upload) akan otomatis <b>terkunci</b> selama preview aktif.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-[#8C9096]">Peran</Label>
              <Select value={role} onValueChange={(v) => { setRole(v); setDivisionId(""); }}>
                <SelectTrigger data-testid="preview-role-select"><SelectValue placeholder="Pilih peran…"/></SelectTrigger>
                <SelectContent>
                  {PREVIEW_ROLES.map((p) => (
                    <SelectItem key={p.role} value={p.role} data-testid={`preview-role-${p.role}`}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {preset?.needsDivision && (
              <div>
                <Label className="text-xs uppercase tracking-widest text-[#8C9096]">Divisi</Label>
                <Select value={divisionId} onValueChange={setDivisionId}>
                  <SelectTrigger data-testid="preview-division-select"><SelectValue placeholder="Pilih divisi…"/></SelectTrigger>
                  <SelectContent>
                    {DIVISIONS.map((d) => (
                      <SelectItem key={d.id} value={d.id} data-testid={`preview-div-${d.short.toLowerCase()}`}>{d.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="text-[11px] text-[#8C9096] flex items-start gap-1.5 p-2 rounded-md bg-[#FAF6EE] border border-[#EAE3D6]">
              <ShieldAlert className="w-3.5 h-3.5 text-brand shrink-0 mt-0.5"/>
              <span>Preview <b>read-only</b>: RBAC backend, token autentikasi, dan data MongoDB tidak pernah tersentuh. Refresh halaman aman — Owner tetap login.</span>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="preview-cancel">Batal</Button>
            <Button onClick={apply} disabled={!canApply} className="bg-brand text-white hover:bg-brand/90" data-testid="preview-apply">Mulai Preview</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

// Banner sticky di atas layout ketika Preview aktif
export const RolePreviewBanner = () => {
  const { viewAs, exitPreview } = useAuth();
  if (!viewAs) return null;
  return (
    <div className="sticky top-0 z-40 bg-gradient-to-r from-[#8B5A2A] via-[#8B262D] to-[#8B5A2A] text-white shadow-md" data-testid="role-preview-banner">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 py-2 flex items-center gap-3 flex-wrap">
        <Eye className="w-4 h-4 shrink-0"/>
        <div className="flex-1 min-w-0 text-xs sm:text-sm">
          <span className="font-semibold">Preview peran: {viewAs.label}</span>
          <span className="opacity-90 hidden sm:inline"> · Simulasi tampilan, data tidak bisa diubah</span>
        </div>
        <button onClick={exitPreview}
          className="inline-flex items-center gap-1.5 h-7 px-3 rounded-md bg-white/15 hover:bg-white/25 text-xs font-semibold transition-colors"
          data-testid="btn-exit-preview">
          <X className="w-3.5 h-3.5"/> Keluar Preview
        </button>
      </div>
      <div className="sm:hidden text-[11px] opacity-90 px-3 pb-1.5">Simulasi tampilan, data tidak bisa diubah</div>
    </div>
  );
};
